import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type PauseReason = 'hover' | 'press' | 'focus' | 'hidden';

/**
 * Minuterie d'un toast : s'arrête au survol, au toucher, au focus et quand la page
 * est masquée, puis reprend là où elle en était. `Infinity` : le toast reste.
 */
export function useToastTimer(duration: number, onEnd: () => void) {
  const [paused, setPaused] = useState(false);
  const reasons = useRef(new Set<PauseReason>());
  const remaining = useRef(duration);
  const endRef = useRef(onEnd);
  useEffect(() => { endRef.current = onEnd; });

  const set = useCallback((reason: PauseReason, on: boolean) => {
    if (on) reasons.current.add(reason);
    else reasons.current.delete(reason);
    setPaused(reasons.current.size > 0);
  }, []);

  useEffect(() => {
    if (!Number.isFinite(duration) || paused) return;
    const startedAt = Date.now();
    const t = window.setTimeout(() => endRef.current(), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(t);
      remaining.current -= Date.now() - startedAt;
    };
  }, [duration, paused]);

  useEffect(() => {
    const onVisibility = () => set('hidden', document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [set]);

  const handlers = {
    onPointerEnter: () => set('hover', true),
    onPointerLeave: () => { set('hover', false); set('press', false); },
    onPointerDown: () => set('press', true),
    onPointerUp: () => set('press', false),
    onPointerCancel: () => set('press', false),
    onFocus: () => set('focus', true),
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) set('focus', false);
    },
  };
  return { paused, handlers };
}
